import { isHydrated, hydrateStore } from 'mobx-persist-store';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { Splash } from '@/components/ui/Splash';
import { chatStore } from '@/features/chat/chat.store';
import { contextCollectionStore } from '@/features/context-collection/context-collection.store';
import { firstRunSummaryStore } from '@/features/context-collection/first-run-summary.store';
import { journalStore } from '@/features/journal/journal.store';
import { routinesStore } from '@/features/routines/routines.state';
import { spritesStore } from '@/features/sprites/sprites.store';
import { subscriptionStore } from '@/features/subscription/subscription.store';
import { tabStore } from '@/features/tabs';
import { uiStore } from '@/stores/ui.store';
import { userStore } from '@/stores/user.store';

// All stores that use makePersistable
const corePersistedStores = [
  userStore,
  uiStore,
  chatStore,
  journalStore,
  contextCollectionStore,
  firstRunSummaryStore,
  subscriptionStore,
  tabStore,
  spritesStore,
  routinesStore,
] as const;
const noAdditionalStores: readonly object[] = [];

interface HydrateMobxStoresProps {
  children: ReactNode;
  fallback?: ReactNode;
  additionalStores?: readonly object[];
}

export function HydrateMobxStores({
  children,
  fallback = <Splash reason="mobx-store-hydration" />,
  additionalStores = noAdditionalStores,
}: HydrateMobxStoresProps) {
  const persistedStores = useMemo(
    () => [...corePersistedStores, ...additionalStores],
    [additionalStores]
  );
  const [, forceRender] = useState(0);
  const isReady = persistedStores.every((store) => isHydrated(store));

  useEffect(() => {
    if (isReady) return;

    let cancelled = false;

    const hydrateAll = async () => {
      await Promise.all(
        persistedStores.map((store) =>
          isHydrated(store) ? Promise.resolve() : hydrateStore(store)
        )
      );
      if (!cancelled) forceRender((version) => version + 1);
    };

    void hydrateAll();

    return () => {
      cancelled = true;
    };
  }, [isReady, persistedStores]);

  if (!isReady) return fallback;
  return children;
}
